import React, { ReactNode } from "react"
import { Box } from "@chakra-ui/react"
import { DioboxEvent } from "sharedTypes"

import useCurrentPlanType from "utilities/useCurrentPlanType"
import { StyleAndConfigurationModalName } from "../../StyleAndConfigurationModals/StyleAndConfigurationModals"
import PortalLogo from "../PreviewBlocks/PortalLogo"
import PreviewBlock from "../PreviewBlock"
import PageHeader from "../PreviewBlocks/PageHeader"
import EventImageAndVideo from "../PreviewBlocks/EventImageAndVideo/EventImageAndVideo"
import EventTitle from "../PreviewBlocks/EventTitle"
import EventDetails from "../PreviewBlocks/EventDetails"
import MapLink from "../PreviewBlocks/MapLink"
import Spacer from "../PreviewBlocks/Spacer"
import TopBar from "./TopBar"
import PoweredByDiobox from "./PoweredByDiobox"

export const PREVIEW_PADDING = 5

type Props = {
  event: DioboxEvent
  onSelectModal: (modalName: StyleAndConfigurationModalName | null) => void
  showSectionOverlays?: boolean
  children?: ReactNode
  /** The languageTemplateId of the form currently being previewed, if any — passed through to
   * blocks (like Map Link) that need to resolve translations the way that specific form's
   * guests would see them, rather than always falling back to the event's default template. */
  languageTemplateId?: string | null
}

const PreviewFrameWithBranding = ({
  event,
  showSectionOverlays,
  onSelectModal,
  children,
  languageTemplateId,
}: Props) => {
  const { corporateFeaturesEnabled } = useCurrentPlanType()

  const {
    whiteLabel, portalBackgroundColor, portalTopNavBackgroundColor,
  } = event

  return (
    <Box w="full" p={showSectionOverlays ? 0 : 10}>
      {!showSectionOverlays && (
        <PreviewBlock isFirstBlock>
          <TopBar />
        </PreviewBlock>
      )}
      <PreviewBlock
        sectionName="Page Logo"
        handleClick={() => onSelectModal(StyleAndConfigurationModalName.PageLogo)}
        showSectionOverlays={showSectionOverlays}
        background={portalTopNavBackgroundColor}
      >
        <PortalLogo event={event} />
      </PreviewBlock>
      <PreviewBlock
        sectionName="Page Header"
        handleClick={() => onSelectModal(StyleAndConfigurationModalName.PageHeader)}
        showSectionOverlays={showSectionOverlays}
        background={portalTopNavBackgroundColor}
      >
        <PageHeader event={event} />
      </PreviewBlock>
      <PreviewBlock
        sectionName="Event Image & Video"
        handleClick={() => onSelectModal(StyleAndConfigurationModalName.EventImageAndVideo)}
        showSectionOverlays={showSectionOverlays}
        background={`linear-gradient(to bottom, ${portalTopNavBackgroundColor} 0px, ${portalTopNavBackgroundColor} 20px, ${portalBackgroundColor} 20px, ${portalBackgroundColor} 100%)`}
      >
        <EventImageAndVideo event={event} />
      </PreviewBlock>
      <PreviewBlock
        sectionName="Public Event Title"
        handleClick={() => onSelectModal(StyleAndConfigurationModalName.PublicEventTitle)}
        showSectionOverlays={showSectionOverlays}
        background={portalBackgroundColor}
      >
        <EventTitle event={event} />
      </PreviewBlock>
      <PreviewBlock
        sectionName="Event Details"
        handleClick={() => onSelectModal(StyleAndConfigurationModalName.EventDetails)}
        showSectionOverlays={showSectionOverlays}
        background={portalBackgroundColor}
      >
        <EventDetails event={event} />
      </PreviewBlock>
      <PreviewBlock
        sectionName="Map Link"
        handleClick={() => onSelectModal(StyleAndConfigurationModalName.MapLink)}
        showSectionOverlays={showSectionOverlays}
        background={portalBackgroundColor}
      >
        <MapLink event={event} languageTemplateId={languageTemplateId} />
      </PreviewBlock>
      <PreviewBlock
        showSectionOverlays={showSectionOverlays}
        background={portalBackgroundColor}
      >
        <Spacer />
      </PreviewBlock>
      <PreviewBlock
        isLastBlock={corporateFeaturesEnabled}
        showSectionOverlays={showSectionOverlays}
        background={portalBackgroundColor}
      >
        <Box w="full" px={PREVIEW_PADDING} pb={4} bg={portalBackgroundColor}>
          {children}
        </Box>
      </PreviewBlock>
      {!corporateFeaturesEnabled && (
        <PreviewBlock
          sectionName="Diobox Branding"
          handleClick={() => onSelectModal(StyleAndConfigurationModalName.DioboxBranding)}
          showSectionOverlays={showSectionOverlays}
          isLastBlock
          background={portalBackgroundColor}
        >
          <PoweredByDiobox whiteLabel={whiteLabel} />
        </PreviewBlock>
      )}
    </Box>
  )
}

export default PreviewFrameWithBranding
